import React, { useCallback, useEffect, useState } from 'react';
import MainHome from '../../components/HomeComponents/MainHome/MainHome';
import FeaturedHomes from '../../components/HomeComponents/FeaturedHomes/FeaturedHomes';
import ChooseUs from '../../components/HomeComponents/ChooseUs/ChooseUs';
import RegisterCta from '../../components/HomeComponents/RegisterCTA/RegisterCta';
import './Home.scss';
import { useDispatch, useSelector } from 'react-redux';
import { UnknownAction } from '@reduxjs/toolkit';
import Loader from '../../components/CommonComponents/Loader/Loader';
import {
  IHome,
  fetchHome,
  setIsFavouriteHome,
  setIsNotFavouriteHome,
} from '../../redux/slices/HomeSlice';
import { toastDefaults } from '../../utils/ToastOptionCommon';
import { toast } from 'react-toastify';
import { IProperty } from '../Listings/Listings';
import { resetListingState } from '../../redux/slices/ListingSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../utils/Permission/Permission';
import { loggedInUserState } from '../../redux/slices/LoginSlice';
import SEO from '../../components/SEO/SEO';
import { Helmet } from 'react-helmet-async';
import SignUpPopupHOC from '../SignUp/hoc/SignupHoc';
import SignUp from '../SignUp/Signup';
import { trackEvent } from '../../utils/Analytics';

interface IProps {
  showSignUpPopup: boolean;
  signUpToggleHandler: () => void;
}

function Home(props: IProps) {
  const { signUpToggleHandler, showSignUpPopup } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [favId, setFavId] = useState<string>('');
  const homeData: IHome = useSelector((state: any) => state.home);
  const {
    postFavPropertyError,
    postFavPropertySuccess,
    deleteFavPropertyError,
    deleteFavPropertySuccess,
    deleteFavPropertyLoading,
    postFavPropertyLoading,
  } = useSelector((state: any) => state.listing);
  const userData = useSelector(loggedInUserState);
  const isLoggedIn = userData ? true : false;
  const { hasPermission } = usePermissions();
  trackEvent('home_page_view');

  //console.log(isLoggedIn);
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    dispatch(
      fetchHome({
        body: {
          // searchText: searchText,
          // Zip: 0,
          // minPrice: minPrice,
          // maxPrice: maxPrice,
          // propertyType: propertyType,
          // minSizeInFt: 1500,
          // status: "available",
          // isActive: true,
          // isFeaturedProperty: true,
          // minRoomPrice: propertyType===2 ? minRoomPrice:0,
          // maxRoomPrice: propertyType===2 ? maxRoomPrice:0,
          // pageNo: 1,
          // pageSize: 10
        },
      }) as unknown as UnknownAction
    );
  }, [isLoggedIn]);

  useEffect(() => {
    if (homeData.error) {
      toast.error('Error Fetching Home', toastDefaults());
    }
  }, [homeData.error]);

  useEffect(() => {
    if (postFavPropertySuccess) {
      toast.success('Marked as Favorite', toastDefaults());
    }
    dispatch(setIsFavouriteHome({ id: favId }));
    dispatch(resetListingState({}));
  }, [postFavPropertySuccess]);

  useEffect(() => {
    if (deleteFavPropertySuccess) {
      toast.success('Unmarked from Favorite', toastDefaults());
    }
    dispatch(setIsNotFavouriteHome({ id: favId }));
    dispatch(resetListingState({}));
  }, [deleteFavPropertySuccess]);

  useEffect(() => {
    if (postFavPropertyError) {
      toast.error('Failed Faving Property', toastDefaults());
    }
    dispatch(resetListingState(null));
  }, [postFavPropertyError]);

  useEffect(() => {
    if (deleteFavPropertyError) {
      toast.error('Failed Un-Faving Property', toastDefaults());
    }
    dispatch(resetListingState(null));
  }, [deleteFavPropertyError]);

  const subscribeEmailState = useSelector(
    (state: any) => state.home.subscribeEmail
  );

  const guestProperties = homeData?.home?.GuestProperties
    ? homeData?.home?.GuestProperties?.map(
        (property: IProperty, index: number) => {
          return {
            id: index + 1,
            _id: property._id,
            title: property.title,
            price: property.p_price ?? 0,
            features: property.amenities,
            availabilityDate: property.available_date ?? [''],
            isNew: false,
            isFeatured: property.isFeaturedProperty ?? false,
            fillImg: property.imagesLink,
            isFavourite: property.isFavoriteForThisUser,
            propertyId: property._id,
            type: property.p_type,
            address: {
              street: property.address?.street ?? '',
              city: property.address?.city ?? '',
              state: property.address?.state ?? '',
            },
          };
        }
      )
    : [];

  const featuredProperties = homeData?.home?.FeaturedProperties
    ? homeData?.home?.FeaturedProperties?.map(
        (property: IProperty, index: number) => {
          return {
            id: index + 1,
            _id: property._id,
            title: property.title,
            price: property.p_price ?? 0,
            features: property.amenities,
            availabilityDate: property.available_date ?? [''],
            isNew: false,
            isFeatured: property.isFeaturedProperty ?? false,
            fillImg: property.imagesLink,
            isFavourite: property.isFavoriteForThisUser,
            propertyId: property._id,
            type: property.p_type,
            address: {
              street: property.address?.street ?? '',
              city: property.address?.city ?? '',
              state: property.address?.state ?? '',
            },
            averageRating: property.averageRating,
            withFood: property.withFood,
          };
        }
      )
    : [];

  // Organization Schema Markup
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'GrhamDeal',
    alternateName: 'Grham Deal',
    url: 'https://grhamdeal.com',
    logo: 'https://grhamdeal.com/logo-512.png', // Update with your actual logo URL
    description:
      'GrhamDeal is a rental property platform connecting tenants and owners across India to find rooms, flats, and houses for rent.',
  };

  return (
    <div className="home">
      {/* Dynamic SEO Meta Tags */}
      <SEO
        title="GrhamDeal | Homes & Rooms for Rent in India"
        description="GrhamDeal is a rental property platform to find rooms, flats, houses and other homes for rent in India. Find verified rental listings and connect with property owners."
        canonical="https://grhamdeal.com"
      />

      {/* JSON-LD Organization Schema Injection */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      {(deleteFavPropertyLoading || postFavPropertyLoading) && <Loader />}

      {showSignUpPopup && (
        <SignUp
          modalClosedSignUp={signUpToggleHandler}
          modalClosedSignIn={signUpToggleHandler}
        />
      )}

      <MainHome />

      <div className="container my-5">
        {/* SECTION 1: Dual Onboarding Cards (Owners vs Tenants) */}
        {!hasPermission('owner') && !hasPermission('tenant') && (
          <div className="row g-4 justify-content-center mb-5">
            {/* For Owners */}
            <div className="col-lg-6">
              <div
                className="h-100 p-4 p-md-5 rounded-4 shadow-sm border position-relative overflow-hidden transition-all"
                style={{
                  background:
                    'linear-gradient(135deg, #f0fdf4 0%, #e6f4f1 100%)',
                  borderColor: '#d1fae5',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow =
                    '0 12px 24px rgba(0,0,0,0.06)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 2px 4px rgba(0,0,0,0.04)';
                }}
              >
                <div className="d-flex flex-column h-100 justify-content-between">
                  <div>
                    <span
                      className="badge px-3 py-1 rounded-pill mb-3 text-uppercase fw-semibold"
                      style={{
                        backgroundColor: 'rgba(5, 150, 105, 0.1)',
                        color: '#059669',
                        fontSize: '11px',
                        letterSpacing: '0.8px',
                      }}
                    >
                      For Property Owners
                    </span>
                    <h4 className="fw-bold text-dark mb-3 fs-3">
                      Register your property for rent
                    </h4>
                    <p className="text-secondary mb-4 fs-6 style={{ lineHeight: '1.6' }}">
                      List your home, room, or apartment and connect with
                      verified, high-quality tenants quickly without broker
                      friction.
                    </p>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-lg px-4 py-2.5 fw-semibold border-0 text-white shadow-sm"
                      style={{
                        backgroundColor: '#059669',
                        borderRadius: '10px',
                        fontSize: '15px',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = '#047857')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = '#059669')
                      }
                      onClick={signUpToggleHandler}
                    >
                      Register Property →
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* For Tenants */}
            <div className="col-lg-6">
              <div
                className="h-100 p-4 p-md-5 rounded-4 shadow-sm border position-relative overflow-hidden transition-all"
                style={{
                  background:
                    'linear-gradient(135deg, #f0f7ff 0%, #e0f2fe 100%)',
                  borderColor: '#bae6fd',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow =
                    '0 12px 24px rgba(0,0,0,0.06)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 2px 4px rgba(0,0,0,0.04)';
                }}
              >
                <div className="d-flex flex-column h-100 justify-content-between">
                  <div>
                    <span
                      className="badge px-3 py-1 rounded-pill mb-3 text-uppercase fw-semibold"
                      style={{
                        backgroundColor: 'rgba(2, 132, 199, 0.1)',
                        color: '#0284c7',
                        fontSize: '11px',
                        letterSpacing: '0.8px',
                      }}
                    >
                      For Tenants
                    </span>
                    <h4 className="fw-bold text-dark mb-3 fs-3">
                      Start chatting directly with owners
                    </h4>
                    <p className="text-secondary mb-4 fs-6 style={{ lineHeight: '1.6' }}">
                      Create your profile, browse verified homes, and get in
                      direct contact with property owners instantly.
                    </p>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-lg px-4 py-2.5 fw-semibold border-0 text-white shadow-sm"
                      style={{
                        backgroundColor: '#0284c7',
                        borderRadius: '10px',
                        fontSize: '15px',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = '#0369a1')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = '#0284c7')
                      }
                      onClick={signUpToggleHandler}
                    >
                      Register Now →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* NEW SECTION 2: Simple 3-Step Process */}
        <div className="row justify-content-center mb-5 py-4">
          <div className="col-12 text-center mb-4">
            <span
              className="badge px-3 py-1 rounded-pill text-uppercase fw-semibold mb-2"
              style={{
                backgroundColor: '#f1f5f9',
                color: '#64748b',
                fontSize: '11px',
                letterSpacing: '0.8px',
              }}
            >
              How It Works
            </span>
            <h3 className="fw-bold text-dark">
              Find your place in 3 easy steps
            </h3>
          </div>

          <div className="col-md-4 text-center mb-4 mb-md-0">
            <div className="p-4 rounded-4 bg-light h-100 border border-light-subtle">
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3 fw-bold fs-5 text-white"
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#328995',
                }}
              >
                1
              </div>
              <h5 className="fw-bold text-dark mb-2">Post Requirements</h5>
              <p className="text-muted small mb-0">
                Tell us your budget, location, and preferred move-in date.
              </p>
            </div>
          </div>

          <div className="col-md-4 text-center mb-4 mb-md-0">
            <div className="p-4 rounded-4 bg-light h-100 border border-light-subtle">
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3 fw-bold fs-5 text-white"
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#328995',
                }}
              >
                2
              </div>
              <h5 className="fw-bold text-dark mb-2">Get Instant Matches</h5>
              <p className="text-muted small mb-0">
                We automatically notify owners matching your exact criteria.
              </p>
            </div>
          </div>

          <div className="col-md-4 text-center">
            <div className="p-4 rounded-4 bg-light h-100 border border-light-subtle">
              <div
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3 fw-bold fs-5 text-white"
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#328995',
                }}
              >
                3
              </div>
              <h5 className="fw-bold text-dark mb-2">Connect Directly</h5>
              <p className="text-muted small mb-0">
                Chat, schedule visits, and seal the deal directly without
                middleman fees.
              </p>
            </div>
          </div>
        </div>

        {/* SECTION 3: Dark CTA Card (Requirement Posting) */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-12">
            <div
              className="position-relative overflow-hidden p-4 p-md-5 rounded-4 border-0 shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                color: '#ffffff',
              }}
            >
              {/* Glow Accent */}
              <div
                className="position-absolute"
                style={{
                  top: '-50%',
                  right: '-10%',
                  width: '320px',
                  height: '320px',
                  background:
                    'radial-gradient(circle, rgba(50, 137, 149, 0.35) 0%, rgba(255,255,255,0) 70%)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                }}
              />

              <div className="row align-items-center position-relative style={{ zIndex: 1 }}">
                <div className="col-md-8 text-center text-md-start mb-4 mb-md-0">
                  <span
                    className="badge px-3 py-1 rounded-pill mb-3 text-uppercase fw-semibold"
                    style={{
                      backgroundColor: 'rgba(52, 211, 153, 0.15)',
                      color: '#34d399',
                      fontSize: '11px',
                      letterSpacing: '0.8px',
                    }}
                  >
                    Need Custom Options?
                  </span>

                  <h3
                    className="fw-bold mb-2 text-white fs-2"
                    style={{ letterSpacing: '-0.5px' }}
                  >
                    Can’t find what you need?
                  </h3>

                  <p
                    className="mb-0 text-light opacity-75 fs-6"
                    style={{ maxWidth: '520px', lineHeight: '1.6' }}
                  >
                    Post your custom requirements, and we'll immediately match
                    you directly with verified local owners.
                  </p>
                </div>

                <div className="col-md-4 text-center text-md-end">
                  <button
                    type="button"
                    className="btn btn-lg px-4 py-3 border-0 fw-semibold shadow-sm text-white"
                    style={{
                      backgroundColor: '#328995',
                      borderRadius: '12px',
                      fontSize: '15px',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#276c76';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow =
                        '0 10px 20px rgba(50, 137, 149, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#328995';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onClick={() => navigate('/housing-inquiry')}
                  >
                    Post Housing Requirement →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* NEW SECTION 4: Trust & Value Proposition Banner */}
        <div
          className="row text-center py-4 px-3 rounded-4 shadow-sm border position-relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderColor: '#e2e8f0',
            transition: 'all 0.3s ease',
          }}
        >
          {/* Affordable Rates */}
          <div className="col-4 border-end border-slate-200 d-flex flex-column justify-content-center px-2">
            <div className="d-flex align-items-center justify-content-center mb-2">
              <span
                className="rounded-circle d-inline-flex align-items-center justify-content-center"
                style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: 'rgba(50, 137, 149, 0.1)',
                  color: '#328995',
                  fontSize: '18px',
                }}
              >
                🏷️
              </span>
            </div>
            <h3
              className="fw-bold text-dark mb-1 fs-4"
              style={{ letterSpacing: '-0.5px' }}
            >
              Best Rates
            </h3>
            <p
              className="text-muted small mb-0 fw-medium"
              style={{ fontSize: '13px' }}
            >
              Affordable & Transparent Pricing
            </p>
          </div>

          {/* Zero Brokerage */}
          <div className="col-4 border-end border-slate-200 d-flex flex-column justify-content-center px-2">
            <div className="d-flex align-items-center justify-content-center mb-2">
              <span
                className="rounded-circle d-inline-flex align-items-center justify-content-center"
                style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: 'rgba(5, 150, 105, 0.1)',
                  color: '#059669',
                  fontSize: '18px',
                }}
              >
                💰
              </span>
            </div>
            <h3
              className="fw-bold text-dark mb-1 fs-4"
              style={{ letterSpacing: '-0.5px' }}
            >
              0%
            </h3>
            <p
              className="text-muted small mb-0 fw-medium"
              style={{ fontSize: '13px' }}
            >
              Zero Commission or Brokerage
            </p>
          </div>

          {/* Direct Contact */}
          <div className="col-4 d-flex flex-column justify-content-center px-2">
            <div className="d-flex align-items-center justify-content-center mb-2">
              <span
                className="rounded-circle d-inline-flex align-items-center justify-content-center"
                style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: 'rgba(2, 132, 199, 0.1)',
                  color: '#0284c7',
                  fontSize: '18px',
                }}
              >
                🤝
              </span>
            </div>
            <h3
              className="fw-bold text-dark mb-1 fs-4"
              style={{ letterSpacing: '-0.5px' }}
            >
              Direct Access
            </h3>
            <p
              className="text-muted small mb-0 fw-medium"
              style={{ fontSize: '13px' }}
            >
              Connect Directly with Owners
            </p>
          </div>
        </div>
      </div>

      {
        <>
          <div
            className={
              homeData.home.FeaturedProperties?.length > 0 ||
              homeData.home.GuestProperties?.length > 0 ||
              homeData.loading
                ? 'featured_section bg-white'
                : ''
            }
          >
            {homeData.loading || subscribeEmailState.loading ? (
              <Loader isHomeLoader={true} />
            ) : (
              <>
                {homeData.home.FeaturedProperties?.length > 0 && (
                  <FeaturedHomes
                    featuredProperties={featuredProperties ?? []}
                    favedId={favId}
                    setFavedId={setFavId}
                  />
                )}
                {/* {homeData.home.GuestProperties?.length > 0 && (
                  <PopularProperties
                    guestProperties={guestProperties ?? []}
                    favedId={favId}
                    setFavedId={setFavId}
                  />
                )} */}
              </>
            )}
          </div>
        </>
      }
      <ChooseUs />
      {/* <Aboutus aboutUsData={homeData.home} /> */}
      {!hasPermission('owner') && !hasPermission('tenant') ? (
        <RegisterCta signUpToggleHandler={signUpToggleHandler} />
      ) : (
        <></>
      )}
    </div>
  );
}

export default SignUpPopupHOC(Home);
